/**
 * Top→bottom network gate for public/editor template pages.
 *
 * Sections still mount (skeletons stay); only *fetches* wait until every
 * prior cascade peer has settled (or a safety timeout advances the head).
 *
 * Used on Home (carousel strips), Products (merch bands), Content (looks).
 */
import React from "react";

const CASCADE_STEP_TIMEOUT_MS = 4000;

type CascadeApi = {
  /** True when this section may start network work. */
  canFetch: (sectionId: string) => boolean;
  /** Call when the section's primary fetch has settled (data or empty). */
  reportSettled: (sectionId: string) => void;
};

const CascadeCtx = React.createContext<CascadeApi | null>(null);

export function useSectionNetworkCascade(): CascadeApi | null {
  return React.useContext(CascadeCtx);
}

function leadingSettledCount(
  ids: readonly string[],
  settled: ReadonlySet<string>,
): number {
  let n = 0;
  for (const id of ids) {
    if (!settled.has(id)) break;
    n += 1;
  }
  return n;
}

/**
 * @param sectionIds Ordered list of section ids that participate (visual top→bottom).
 * @param enabled When false, every section can fetch immediately (no-op gate).
 */
export function SectionNetworkCascadeProvider({
  sectionIds,
  enabled = true,
  children,
}: {
  sectionIds: readonly string[];
  enabled?: boolean;
  children: React.ReactNode;
}) {
  const ids = React.useMemo(
    () => sectionIds.map((id) => String(id || "").trim()).filter(Boolean),
    [sectionIds],
  );
  const idsKey = ids.join("|");

  /**
   * How many leading sections have settled. Section at index `head` may fetch;
   * sections after `head` wait.
   */
  const [head, setHead] = React.useState(0);
  const settledRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    settledRef.current = new Set();
    setHead(0);
  }, [idsKey]);

  // Safety: advance one step if the current head never reports.
  React.useEffect(() => {
    if (!enabled || ids.length <= 1) return;
    if (head >= ids.length) return;
    const currentId = ids[head];
    if (!currentId) return;
    const t = window.setTimeout(() => {
      if (settledRef.current.has(currentId)) return;
      settledRef.current.add(currentId);
      setHead(leadingSettledCount(ids, settledRef.current));
    }, CASCADE_STEP_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [enabled, ids, head, idsKey]);

  const api = React.useMemo<CascadeApi>(() => {
    if (!enabled || ids.length <= 1) {
      return {
        canFetch: () => true,
        reportSettled: () => {},
      };
    }
    return {
      canFetch: (sectionId: string) => {
        const id = String(sectionId || "").trim();
        const idx = ids.indexOf(id);
        if (idx < 0) return true;
        return idx <= head;
      },
      reportSettled: (sectionId: string) => {
        const id = String(sectionId || "").trim();
        if (!id || settledRef.current.has(id)) return;
        if (ids.indexOf(id) < 0) return;
        settledRef.current.add(id);
        setHead(leadingSettledCount(ids, settledRef.current));
      },
    };
  }, [enabled, ids, head]);

  return <CascadeCtx.Provider value={api}>{children}</CascadeCtx.Provider>;
}
